import React from 'react';
import { FiX, FiEdit } from 'react-icons/fi';

const PurchaseBillModal = ({ bill, onClose, onEdit }) => {
  if (!bill) return null;

  console.log("📦 PurchaseBillModal received bill 👉", bill);

  const getPaymentStatusBadge = (status) => {
    const badges = {
      unpaid: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-800">Purchase Bill Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Bill Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Bill Number</h3>
              <p className="text-lg font-semibold text-gray-900">{bill.bill_number}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">Bill Date</h3>
              <p className="text-lg font-semibold text-gray-900">
                {bill.bill_date && new Date(bill.bill_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          <hr />

          {/* Supplier Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Supplier Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Name:</span>
                <p className="font-medium text-gray-900">
                  {bill.supplier_display_name || bill.supplier_name}
                </p>
              </div>
              {bill.supplier_company && (
                <div>
                  <span className="text-sm text-gray-600">Company:</span>
                  <p className="font-medium text-gray-900">{bill.supplier_company}</p>
                </div>
              )}
              {bill.supplier_contact && (
                <div>
                  <span className="text-sm text-gray-600">Contact:</span>
                  <p className="font-medium text-gray-900">{bill.supplier_contact}</p>
                </div>
              )}
              {bill.supplier_email && (
                <div>
                  <span className="text-sm text-gray-600">Email:</span>
                  <p className="font-medium text-gray-900">{bill.supplier_email}</p>
                </div>
              )}
              {bill.supplier_gstin && (
                <div>
                  <span className="text-sm text-gray-600">GSTIN:</span>
                  <p className="font-medium text-gray-900">{bill.supplier_gstin}</p>
                </div>
              )}
              {bill.supplier_address && (
                <div className="md:col-span-2">
                  <span className="text-sm text-gray-600">Address:</span>
                  <p className="font-medium text-gray-900">{bill.supplier_address}</p>
                </div>
              )}
            </div>
          </div>

          {/* Bill Items */}
          {bill.items && bill.items.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill Items</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">HSN no.</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bill.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {item.item_name || item.product_name}
                          
                            <span className="text-xs text-gray-500 block"># {item.item_code}</span>
                          
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{item.hsn_sac || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{item.description || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          {parseFloat(item.quantity).toFixed(2)} {item.unit}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          ₹{parseFloat(item.rate).toFixed(2)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          {parseFloat(item.tax_rate || 0).toFixed(0)}%
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                          ₹{parseFloat(item.amount).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Amount Details */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Amount Details</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">₹{parseFloat(bill.subtotal || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tax Amount:</span>
                <span className="font-medium">₹{parseFloat(bill.tax_amount || 0).toFixed(2)}</span>
              </div>
              {parseFloat(bill.discount_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount:</span>
                  <span className="font-medium text-red-600">-₹{parseFloat(bill.discount_amount || 0).toFixed(2)}</span>
                </div>
              )}
              <hr />
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Total Amount:</span>
                <span className="font-bold text-green-600">₹{parseFloat(bill.total_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Paid Amount:</span>
                <span className="font-medium text-green-600">₹{parseFloat(bill.paid_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due Amount:</span>
                <span className="font-medium text-red-600">₹{parseFloat(bill.due_amount || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <span className="text-sm text-gray-600">Payment Status:</span>
              <p className="mt-1">{getPaymentStatusBadge(bill.payment_status)}</p>
            </div>
            {bill.payment_mode && (
              <div className="bg-gray-50 rounded-lg p-4">
                <span className="text-sm text-gray-600">Payment Mode:</span>
                <p className="font-medium text-gray-900 capitalize mt-1">{bill.payment_mode}</p>
              </div>
            )}
          </div>

          {/* Delivery Address */}
          {(bill.delivery_address || bill.delivery_city || bill.delivery_state) && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Delivery Address</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700">
                  {[bill.delivery_address, bill.delivery_city, bill.delivery_state, bill.delivery_pincode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          {bill.notes && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Notes</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                <p className="text-gray-700">{bill.notes}</p>
              </div>
            </div>
          )}

          {/* Terms */}
          {bill.terms && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Terms & Conditions</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap">{bill.terms}</p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {onEdit && (
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <FiEdit size={16} />
              Edit Bill
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseBillModal;
